import React from 'react';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import Immutable from 'immutable';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import latestSemver from 'latest-semver';

import Copiable from '@mapbox/mr-ui/copiable';
import ChevronousText from '@mapbox/mr-ui/chevronous-text';
import LoaderLocal from '@mapbox/mr-ui/loader-local';
import CodeSnippet from '@mapbox/mr-ui/code-snippet';
import UnderlineTabs from '@mapbox/mr-ui/underline-tabs';

import {
  NewTabLink,
  PageWrapper,
  PageShell,
  DownloadGroup
} from '../components';
import { store } from '../redux/store';
import {
  getInstallerVersions,
  getDockerVersions,
  getKubernetesVersions,
  getLicenseKey
} from '../redux/action-creators';
import { getUser } from '../util/mapbox-client';
import { ATLAS_V3_MIN_INSTALLER_VERSION } from '../config';

const DockerInstructions = ({
  installerSoftwareInfo,
  latestInstaller,
  visible,
  minVersion
}) => (
  <div className={visible ? '' : 'none'}>
    <div className="prose mb24 mt12">
      <p>
        To get started with Atlas for Docker Compose, download the Atlas
        installer below. The Atlas installer is a command-line utility that lets
        you install, upgrade, configure, and uninstall Atlas.
      </p>
      <p>
        Choose an installer version and click the download button for the
        correct system. After the installer has downloaded, head to the{' '}
        <NewTabLink
          href="https://docs.mapbox.com/atlas/guides/"
          className="link"
          track="Clicked to view Atlas docs"
        >
          Atlas documentation
        </NewTabLink>{' '}
        to learn how to install, configure, run, and use Atlas.
      </p>
    </div>
    <div className="mt24 px24 py24 border border--gray-light round">
      <DownloadGroup
        installerSoftwareInfo={installerSoftwareInfo}
        latestInstaller={latestInstaller}
        minVersion={minVersion}
      />
    </div>
  </div>
);

const K8sInstructions = ({ visible }) => (
  <div className={visible ? '' : 'none'}>
    <div className="prose mb24 mt12">
      <p>
        To get started with Atlas for Kubernetes, you can prefetch the Helm
        charts as shown below. Next, head to the{' '}
        <NewTabLink
          href="https://docs.mapbox.com/atlas/guides/"
          className="link"
          track="Clicked to view Atlas documentation"
        >
          Atlas documentation
        </NewTabLink>{' '}
        to learn how to install, configure, run, and use Atlas.
      </p>
    </div>
    <CodeSnippet
      code={`
# add the Mapbox Helm repo
$ export MAPBOX_ACCESS_TOKEN=&lt;your-atlas-sk-token&gt;
$ helm repo add mapbox "https://api.mapbox.com/v2/software/charts?access_token=$MAPBOX_ACCESS_TOKEN"

# pre-fetch the chart
$ helm fetch mapbox/mapbox-atlas
`}
    />
  </div>
);

const v2Tabs = [
  {
    label: 'Kubernetes',
    id: 'k8s'
  },
  { label: 'Docker Compose', id: 'docker' }
];

const v3Tabs = [
  { label: 'Maps', id: 'maps' },
  { label: 'Search & Navigation', id: 'search-nav' }
];

const V3MapsInstructions = ({
  installerSoftwareInfo,
  latestInstaller,
  visible
}) => (
  <div className={visible ? '' : 'none'}>
    <div className="prose mb24 mt12">
      <p>
        To get started with Atlas v3 for Maps, download the Atlas installer
        below. The Atlas installer is a command-line utility that lets you
        install, upgrade, configure, and uninstall Atlas.
      </p>
      <p>
        Choose an installer version and click the download button for the
        correct system. After the installer has downloaded, head to the{' '}
        <NewTabLink
          href="https://docs.mapbox.com/atlas/guides/"
          className="link"
          track="Clicked to view Atlas docs"
        >
          Atlas documentation
        </NewTabLink>{' '}
        to learn how to install, configure, run, and use Atlas.
      </p>
    </div>
    <div className="mt24 px24 py24 border border--gray-light round">
      <DownloadGroup
        installerSoftwareInfo={installerSoftwareInfo}
        latestInstaller={latestInstaller}
        minVersion={ATLAS_V3_MIN_INSTALLER_VERSION}
      />
    </div>
  </div>
);

const ReleaseChip = ({ date, version }) => (
  <div className="txt-s mt-neg18" title={`v${version} - ${displayDate(date)}`}>
    v{version}
  </div>
);

const LegacyLink = ({ canAccessAtlasV1 }) =>
  canAccessAtlasV1 && (
    <li className="mb12">
      <Link to="/atlas-server" className="link">
        <ChevronousText text="Legacy Atlas Server" />
      </Link>
    </li>
  );

let Atlas = class Atlas extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      activeTab: 'k8s',
      activeV3Tab: 'maps',
      activeV3SearchNavTab: 'k8s'
    };
  }

  changeTab = id => this.setState({ activeTab: id });
  changeV3Tab = id => this.setState({ activeV3Tab: id });
  changeV3SearchNavTab = id => this.setState({ activeV3SearchNavTab: id });

  fetchData() {
    return Promise.all([
      getUser(),
      store.dispatch(getInstallerVersions()),
      store.dispatch(getDockerVersions()),
      store.dispatch(getKubernetesVersions()),
      store.dispatch(getLicenseKey())
    ]);
  }

  componentDidMount() {
    this.fetchData();
  }

  getSidebarSections(loading = false) {
    const {
      latestDocker,
      latestInstaller,
      latestKubernetes,
      canAccessAtlasV1
    } = this.props;

    return [
      { heading: 'Latest Atlas versions', body: <div /> },
      {
        heading: <span className="txt-m">Kubernetes</span>,
        body: loading ? (
          <LoaderLocal />
        ) : (
          <ReleaseChip
            date={latestKubernetes.get('created')}
            version={latestKubernetes.get('version')}
          />
        )
      },
      {
        heading: <span className="txt-m">Docker Compose</span>,
        body: loading ? (
          <LoaderLocal />
        ) : (
          <ReleaseChip
            date={latestDocker.get('date')}
            version={latestDocker.get('version')}
          />
        )
      },
      {
        heading: <span className="txt-m">Atlas installer</span>,
        body: loading ? (
          <LoaderLocal />
        ) : (
          <ReleaseChip
            date={latestInstaller.get('date')}
            version={latestInstaller.get('version')}
          />
        )
      },
      {
        heading: <span className="txt-m">License key</span>,
        body: loading ? (
          <LoaderLocal />
        ) : (
          <div className="mt-neg12">
            <Copiable value={this.props.licenseKey} truncated={true} />
          </div>
        )
      },
      {
        body: (
          <div className="my24 color-gray txt-s border-t border-t--2 border--gray-faint" />
        )
      },
      {
        body: (
          <ul>
            <li className="mb12">
              <NewTabLink
                href="https://docs.mapbox.com/atlas/guides/"
                className="link"
                track="Clicked to view Atlas documentation"
              >
                <ChevronousText text="Atlas documentation" />
              </NewTabLink>
            </li>
            <LegacyLink canAccessAtlasV1={canAccessAtlasV1} />
            <li className="mb12">
              <NewTabLink
                href="https://www.mapbox.com/contact/"
                className="link"
                track="Clicked contact us from Atlas page"
              >
                <ChevronousText text="Contact us" />
              </NewTabLink>
            </li>
          </ul>
        )
      }
    ];
  }

  renderLegacy = () =>
    this.props.canAccessAtlasV1 && (
      <li className="mb12">
        <Link to="/atlas-server" className="link">
          <ChevronousText text="Legacy Atlas Server" />
        </Link>
      </li>
    );

  render() {
    return (
      <PageShell>
        <PageWrapper
          sectionName="Atlas"
          sidebarSections={this.getSidebarSections(
            !this.props.latestInstaller || !this.props.latestDocker
          )}
        >
          <div>
            <p className="mb24">
              With Atlas you can run Mapbox maps, search, or navigation on your
              own infrastructure, including behind a firewall or on airgapped
              networks.
            </p>
            <h2 className="txt-h3 mb12">Atlas v3</h2>
            <p className="mb24">
              Atlas v3 enables containerless deployments of maps and uses fewer
              containers for search and navigation.
            </p>
            <div className="border-b border--gray-light ">
              <UnderlineTabs
                items={v3Tabs}
                active={this.state.activeV3Tab}
                onChange={this.changeV3Tab}
                overlapBorder
              />
            </div>
            {this.props.latestInstaller && this.props.latestDocker && (
              <>
                <V3MapsInstructions
                  {...this.props}
                  visible={this.state.activeV3Tab === 'maps'}
                />
                <div
                  className={
                    this.state.activeV3Tab === 'search-nav' ? '' : 'none'
                  }
                >
                  <div className="prose mb24 mt12">
                    <p>
                      Atlas v3 Search & Navigation supports two deployment
                      options: Kubernetes (including OpenShift, EKS, and others)
                      and Docker Compose.
                    </p>
                  </div>
                  <div className="border-b border--gray-light mt12">
                    <UnderlineTabs
                      items={v2Tabs}
                      active={this.state.activeV3SearchNavTab}
                      onChange={this.changeV3SearchNavTab}
                      overlapBorder
                    />
                  </div>
                  <K8sInstructions
                    {...this.props}
                    visible={this.state.activeV3SearchNavTab === 'k8s'}
                  />
                  <DockerInstructions
                    {...this.props}
                    visible={this.state.activeV3SearchNavTab !== 'k8s'}
                    minVersion={ATLAS_V3_MIN_INSTALLER_VERSION}
                  />
                </div>
              </>
            )}
            <h2 className="txt-h3 mt36 mb12">Atlas v2</h2>
            <p className="mb24">
              Atlas v2 comes in two main flavors — Kubernetes (including
              OpenShift, EKS, and others) and Docker Compose. Choose the flavor
              you'll be using to get started.
            </p>
            <div className="border-b border--gray-light ">
              <UnderlineTabs
                items={v2Tabs}
                active={this.state.activeTab}
                onChange={this.changeTab}
                overlapBorder
              />
            </div>
            {this.props.latestInstaller && this.props.latestDocker && (
              <>
                <K8sInstructions
                  {...this.props}
                  visible={this.state.activeTab === 'k8s'}
                />
                <DockerInstructions
                  {...this.props}
                  visible={this.state.activeTab !== 'k8s'}
                />
              </>
            )}

            <hr />
            <div className="my24 color-gray txt-s border-t border-t--2 border--gray-faint">
              <p className="my12">
                Copyright © {dayjs().year()} Mapbox, Inc., all rights reserved.
                Atlas Software is Commercial Computer Software. Use Governed by{' '}
                <NewTabLink
                  href="https://www.mapbox.com/legal/enterprise-tos/"
                  className="link"
                  track="Clicked on Atlas Terms of Service"
                >
                  Mapbox Atlas Server Terms of Service
                </NewTabLink>
                , as of the date of (and as modified by) our Agreement with you.
              </p>
            </div>
          </div>
        </PageWrapper>
      </PageShell>
    );
  }
};

Atlas.propTypes = {
  installerSoftwareInfo: PropTypes.objectOf(Immutable.Map),
  dockerSoftwareInfo: PropTypes.objectOf(Immutable.Map)
};

function mapStateToProps(state) {
  const k8sVersions = Object.values(
    state.get('atlasKubernetesSoftwareInfo').toJS().entries || { entries: [] }
  ).flatMap(b => b.map(a => ({ version: a.version, created: a.created })));
  const latestK8sSemver = latestSemver(k8sVersions.map(a => a.version));
  const latestKubernetes = Immutable.Map(
    k8sVersions.find(a => a.version === latestK8sSemver)
  );

  const user = state.get('user').get('atlasv2') || Immutable.Map();
  const features = user.get('features') || [];
  const canAccessAtlasV1 = features.includes('unprotected_builds');
  return {
    canAccessAtlasV1: canAccessAtlasV1,
    atlasV1: state.get('atlasServerSoftwareInfo', Immutable.Map()),
    installerSoftwareInfo: state.get(
      'atlasInstallerSoftwareInfo',
      Immutable.Map()
    ),
    latestInstaller: state
      .get('atlasInstallerSoftwareInfo', Immutable.Map())
      .getIn(['versions', 0]),
    dockerSoftwareInfo: state.get('atlasDockerSoftwareInfo', Immutable.Map()),
    latestDocker: state
      .get('atlasDockerSoftwareInfo', Immutable.Map())
      .getIn(['versions', 0]),
    licenseKey: state.get('licenseKey'),
    latestKubernetes: latestKubernetes
  };
}

Atlas = connect(mapStateToProps)(Atlas);

export { Atlas };

function displayDate(raw) {
  return dayjs(raw).format('YYYY-MM-DD');
}
